import type { CrmObject } from '@/types/crm';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { HighlightCardShell } from './HighlightCardShell';
import { LAST_EVENT_ICON } from './highlightIcons';
import { useHighlightLastEvent } from './useHighlightLastEvent';
import { useOpenHighlightTarget } from './useOpenHighlightTarget';

/**
 * Accessible names for the tile's click target, keyed by the entity type the
 * event lives on. Naming the destination ("email thread") beats echoing the
 * label ("Open last emailed"), which reads as a typo to a screen-reader user.
 */
const ACTION_LABEL_BY_TARGET_TYPE: Record<string, string> = {
  ahoy_email_thread: 'Open last email thread',
  ahoy_meeting: 'Open last meeting',
};

interface LastEventHighlightCardProps {
  object: CrmObject;
  definition: Extract<HighlightDefinition, { kind: 'last_event' }>;
  orgId: number;
}

export function LastEventHighlightCard({
  object,
  definition,
  orgId,
}: LastEventHighlightCardProps) {
  const result = useHighlightLastEvent(orgId, object, definition);
  // Null whenever the metric resolved to nothing (or to this same record) —
  // the tile then stays a plain, non-focusable display.
  const handleOpen = useOpenHighlightTarget(
    result.targetEntityId,
    result.targetEntityTypeName
  );

  const actionLabel = handleOpen
    ? (ACTION_LABEL_BY_TARGET_TYPE[result.targetEntityTypeName ?? ''] ??
      `Open ${definition.label.toLowerCase()}`)
    : undefined;

  return (
    <HighlightCardShell
      label={definition.label}
      result={result}
      icon={LAST_EVENT_ICON}
      emptyText='Never'
      onClick={handleOpen}
      actionLabel={actionLabel}
    />
  );
}
